/**
 * The three states of a loaded screen, rendered once.
 *
 * Pages that hand-roll this leave the error case out — which is how a silent
 * blank screen ships. Passing a `useAsync` result through here makes all three
 * states impossible to forget, because there is nowhere to put the content
 * except inside the branch that has it.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import type { AsyncState } from '../hooks/useAsync';
import { ErrorState } from './ErrorState';

export interface AsyncContentProps<T> {
  state: AsyncState<T>;
  /** Rendered only with a value in hand — `data` is never `undefined` in here. */
  children: (data: T) => ReactNode;
  /** `rows` draws list-shaped skeletons; `spinner` is for a small area. */
  placeholder?: 'rows' | 'spinner';
  /** How many skeleton rows. Ignored unless `placeholder` is `rows`. */
  rows?: number;
}

export function AsyncContent<T>({
  state,
  children,
  placeholder = 'rows',
  rows = 4,
}: AsyncContentProps<T>) {
  // Error first: a reload that fails still has the previous data, and showing
  // stale content under no indication of failure is worse than showing neither.
  if (state.error) {
    return <ErrorState error={state.error} onRetry={state.reload} />;
  }

  // Loading only when there is nothing to show. A reload with data already on
  // screen keeps it — flashing back to skeletons on every refresh is the
  // jarring part of "live" interfaces, and the data is not wrong yet.
  if (state.data === undefined) {
    if (placeholder === 'spinner') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    return (
      <Stack spacing={1} sx={{ p: 1 }}>
        {Array.from({ length: rows }, (_, index) => (
          <Skeleton key={index} variant="rounded" height={44} />
        ))}
      </Stack>
    );
  }

  return <>{children(state.data)}</>;
}
